/**
 * usePermissions Hook
 *
 * Provides permission checking functionality throughout the app
 * Reads permissions dynamically from backend API
 *
 * IMPORTANT: Uses centralized query keys from permissionsQueryKeys.ts
 * This ensures consistent cache invalidation across the application.
 *
 * PROJECT CONTEXT: When a project is selected, this hook fetches
 * project-level permissions which override company-level permissions.
 * This enables the sidebar to reflect project-specific access rules.
 */

import React, { useMemo } from 'react';
import { useUser } from '../contexts/UserContext';
import { useTenantSafe } from '../contexts/TenantContext';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import {
  UserRole,
  Permission,
  isOwlyAdmin,
  isCompanyAdmin,
  isProjectManager,
  isSalesManager,
  isSalesAgent,
  canManageCompanies,
  canManageProjects,
  hasGlobalConfigAccess,
} from '../types/sgi-roles.types';
import { permissionsKeys } from '@/modules/users/hooks/permissionsQueryKeys';

export interface UsePermissionsReturn {
  // Permission checking
  hasPermission: (permission: Permission | string) => boolean;
  hasAnyPermission: (permissions: (Permission | string)[]) => boolean;
  hasAllPermissions: (permissions: (Permission | string)[]) => boolean;

  /**
   * Check submodule permission with DENY-BY-DEFAULT behavior.
   *
   * SECURITY: Returns true ONLY if permission is explicitly set to true.
   * Returns false if:
   * - Permissions haven't loaded yet
   * - Module doesn't exist in permissions
   * - Submodule permission is not found
   * - Permission is explicitly false
   *
   * This ensures users only see content they have explicit permission for.
   */
  hasSubmodulePermission: (module: string, submodule: string) => boolean;

  // Role checking
  isOwlyAdmin: boolean;
  isCompanyAdmin: boolean;
  isProjectManager: boolean;
  isSalesManager: boolean;
  isSalesAgent: boolean;

  // Feature access
  canManageCompanies: boolean;
  canManageProjects: boolean;
  hasGlobalConfigAccess: boolean;

  // Current role and permissions
  currentRole: UserRole;
  allPermissions: Permission[];

  // Loading state
  isLoading: boolean;
}

/**
 * Hook to check permissions for the current user
 *
 * @example
 * ```tsx
 * const { hasPermission, isOwlyAdmin, canManageProjects } = usePermissions();
 *
 * if (hasPermission('gestion.view')) {
 *   // Show Gestión section
 * }
 *
 * if (isOwlyAdmin) {
 *   // Show admin panel
 * }
 * ```
 */
export const usePermissions = (): UsePermissionsReturn => {
  const { currentUser } = useUser();
  const { selectedProject } = useTenantSafe();

  // Get current project ID for project-scoped permissions
  // Uses TenantContext (the active context) instead of legacy ProjectContext
  // This ensures sidebar permissions are always in sync with the selected project
  const currentProjectId = selectedProject?.id || null;

  // Get current role - use SGI role if available, fallback to legacy role
  const currentRole = useMemo((): UserRole => {
    // If user has sgiRole property, use it
    if ('sgiRole' in currentUser && currentUser.sgiRole) {
      return currentUser.sgiRole as UserRole;
    }

    // Map legacy roles to SGI roles
    const legacyRoleMap: Record<string, UserRole> = {
      president: UserRole.PRESIDENT,
      manager: UserRole.MANAGER,
      agent: UserRole.AGENT,
      admin: UserRole.OWLY_ADMIN,
      project_manager: UserRole.PROJECT_MANAGER,
      sales_leader: UserRole.SALES_MANAGER,
      sales_advisor: UserRole.SALES_AGENT,
    };

    return legacyRoleMap[currentUser.role] || UserRole.SALES_AGENT;
  }, [currentUser]);

  // Type for backend permissions response: { module: { action: boolean } }
  type BackendPermissions = Record<string, Record<string, boolean>>;

  // Fetch permissions from backend API
  // Uses centralized query key for consistent cache management
  // When a project is selected, includes project param for project-level permissions
  const { data: backendPermissions, isLoading } = useQuery<BackendPermissions>({
    // Include project ID in query key so cache is project-specific
    queryKey: currentProjectId
      ? [...permissionsKeys.me(), 'project', currentProjectId] as const
      : permissionsKeys.me(),
    queryFn: async () => {
      // Build endpoint URL with optional project parameter
      const endpoint = currentProjectId
        ? `/auth/permissions/me/?project=${currentProjectId}`
        : '/auth/permissions/me/';
      const data = await apiClient.get<BackendPermissions>(endpoint)
      return (data as BackendPermissions) || {}
    },
    // Keep permissions warm across component re-mounts for 5 minutes. Permission
    // saves explicitly call `queryClient.invalidateQueries(permissionsKeys.me())`
    // (see useProjectPermissions.ts), which forces a refetch regardless of
    // staleTime — so real changes still propagate instantly. staleTime=0 +
    // refetchOnMount='always' was causing every component that reads
    // usePermissions() to refire the request on mount, producing 5-10×
    // duplicate /auth/permissions/me/ hits on every page load.
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: !!currentUser,
    refetchOnWindowFocus: true, // Refetch when user returns to the tab
  });

  // Get all permissions for current role (as string array)
  const allPermissions = useMemo(() => {
    if (!backendPermissions) return [];

    const permissions: Permission[] = [];
    Object.entries(backendPermissions).forEach(([module, actions]) => {
      Object.entries(actions).forEach(([action, hasPermission]) => {
        if (hasPermission) {
          permissions.push(`${module}.${action}` as Permission);
        }
      });
    });
    return permissions;
  }, [backendPermissions]);

  /**
   * Check if user has a specific permission
   * Accepts either "module.action" string or Permission enum
   */
  const hasPermissionFn = useMemo(
    () => (permission: Permission | string) => {
      // If permissions haven't loaded yet, deny access by default
      if (!backendPermissions) {
        return false;
      }

      // Parse permission string (e.g., "gestion.view" -> module="gestion", action="view")
      const [module, action] = permission.split('.');

      if (!module || !action) {
        console.warn(`Invalid permission format: ${permission}. Expected "module.action"`);
        return false;
      }

      // Check permissions
      return backendPermissions[module]?.[action] === true;
    },
    [backendPermissions]
  );

  const hasAnyPermissionFn = useMemo(
    () => (permissions: (Permission | string)[]) => {
      return permissions.some((permission) => hasPermissionFn(permission));
    },
    [hasPermissionFn]
  );

  const hasAllPermissionsFn = useMemo(
    () => (permissions: (Permission | string)[]) => {
      return permissions.every((permission) => hasPermissionFn(permission));
    },
    [hasPermissionFn]
  );

  /**
   * Check submodule permission with DENY-BY-DEFAULT behavior.
   *
   * SECURITY: This function now implements deny-by-default:
   * - Returns false if permissions haven't loaded yet
   * - Returns false if module/submodule permission is not found
   * - Returns true ONLY if permission is explicitly set to true
   *
   * This prevents users from seeing content they shouldn't have access to
   * while permissions are loading or if permissions are misconfigured.
   */
  const hasSubmodulePermissionFn = useMemo(
    () => (module: string, submodule: string) => {
      // DENY-BY-DEFAULT: If permissions haven't loaded, deny access
      if (!backendPermissions) {
        return false;
      }

      const modulePerms = backendPermissions[module];

      // DENY-BY-DEFAULT: If no permissions for this module, deny access
      if (!modulePerms) {
        return false;
      }

      // Check if submodule permission exists
      const submoduleValue = modulePerms[submodule];

      // DENY-BY-DEFAULT: If submodule permission doesn't exist, deny access
      if (submoduleValue === undefined) {
        return false;
      }

      // Return true ONLY if explicitly set to true
      return submoduleValue === true;
    },
    [backendPermissions]
  );

  // Role checking
  const isOwlyAdminFlag = useMemo(() => isOwlyAdmin(currentRole), [currentRole]);
  const isCompanyAdminFlag = useMemo(() => isCompanyAdmin(currentRole), [currentRole]);
  const isProjectManagerFlag = useMemo(() => isProjectManager(currentRole), [currentRole]);
  const isSalesManagerFlag = useMemo(() => isSalesManager(currentRole), [currentRole]);
  const isSalesAgentFlag = useMemo(() => isSalesAgent(currentRole), [currentRole]);

  // Feature access
  const canManageCompaniesFlag = useMemo(() => canManageCompanies(currentRole), [currentRole]);
  const canManageProjectsFlag = useMemo(() => canManageProjects(currentRole), [currentRole]);
  const hasGlobalConfigAccessFlag = useMemo(() => hasGlobalConfigAccess(currentRole), [currentRole]);

  return {
    // Permission checking
    hasPermission: hasPermissionFn,
    hasAnyPermission: hasAnyPermissionFn,
    hasAllPermissions: hasAllPermissionsFn,
    hasSubmodulePermission: hasSubmodulePermissionFn,

    // Role checking
    isOwlyAdmin: isOwlyAdminFlag,
    isCompanyAdmin: isCompanyAdminFlag,
    isProjectManager: isProjectManagerFlag,
    isSalesManager: isSalesManagerFlag,
    isSalesAgent: isSalesAgentFlag,

    // Feature access
    canManageCompanies: canManageCompaniesFlag,
    canManageProjects: canManageProjectsFlag,
    hasGlobalConfigAccess: hasGlobalConfigAccessFlag,

    // Current role and permissions
    currentRole,
    allPermissions,

    // Loading state
    isLoading,
  };
};

/**
 * HOC to wrap components with permission checking
 *
 * @example
 * ```tsx
 * const AdminOnlyComponent = withPermission(
 *   MyComponent,
 *   Permission['companies:create']
 * );
 * ```
 */
export function withPermission<P extends object>(
  Component: React.ComponentType<P>,
  requiredPermission: Permission
): React.FC<P> {
  return function PermissionWrappedComponent(props: P) {
    const { hasPermission } = usePermissions();

    if (!hasPermission(requiredPermission)) {
      return null;
    }

    return <Component {...props} />;
  };
}

/**
 * HOC to wrap components with role checking
 *
 * @example
 * ```tsx
 * const AdminOnlyComponent = withRole(MyComponent, UserRole.OWLY_ADMIN);
 * ```
 */
export function withRole<P extends object>(
  Component: React.ComponentType<P>,
  requiredRole: UserRole
): React.FC<P> {
  return function RoleWrappedComponent(props: P) {
    const { currentRole } = usePermissions();

    if (currentRole !== requiredRole) {
      return null;
    }

    return <Component {...props} />;
  };
}
